import { ReactNode } from "react";
import { Box } from "@mui/material";
import { useParams } from "react-router";
import SegmentedControl from "@common/components/segmentedControl/SegmentedControl";
import { useTranslation } from "@localization/useTranslation";
import { BulkAssessSelection } from "./useBulkAssessSelection";
import { BulkMitigateSelection } from "./useBulkMitigateSelection";
import { RiskOperationalSectionState } from "./RiskOperationalOverview.hooks";
import { buildRiskOperationalSectionViews } from "./RiskOperationalOverview.types";
import EntityAssessmentsTable from "./entityAssessmentsTable/EntityAssessmentsTable";
import ScenarioAssessmentsTable from "../riskAssessmentsOverview/scenarioAssessmentsTable/ScenarioAssessmentsTable";

interface RiskOperationalOverviewProps {
    bulkAssess: BulkAssessSelection;
    bulkMitigate: BulkMitigateSelection;
    // Owned by the risk area page, which renders the bulk buttons and shows them only while the entity table is up.
    section: RiskOperationalSectionState;
    // The bulk buttons act on the entity table, so the risk area page passes them here instead of putting them on
    // its tab row when the area has a section switcher.
    sectionButtons: ReactNode;
}

// The Operational tab of a risk area. Which table it shows follows the area's assessment level, and an area
// assessed at both levels gets a switcher to move between them. Both tables fetch their own data, so this only
// resolves the area from the route.
function RiskOperationalOverview({ bulkAssess, bulkMitigate, section, sectionButtons }: RiskOperationalOverviewProps) {
    const { translateString } = useTranslation();
    const { riskAreaId } = useParams<{ riskAreaId: string }>();

    if (!riskAreaId) return null;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 2 }}>
            {section.canSwitchSections && (
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <SegmentedControl
                        options={buildRiskOperationalSectionViews(translateString)}
                        selected={section.selectedSection}
                        onSelect={section.onSelectSection}
                    />
                    {sectionButtons}
                </Box>
            )}

            {section.showEntities && <EntityAssessmentsTable riskAreaId={riskAreaId} bulkAssess={bulkAssess} bulkMitigate={bulkMitigate} />}

            {section.showOrganisation && (
                <ScenarioAssessmentsTable key={`${riskAreaId}:organisation`} riskAreaId={riskAreaId} subject={{ kind: "organisation" }} />
            )}
        </Box>
    );
}

export default RiskOperationalOverview;
